import { useCallback, useRef, useState, type ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';

import {
  ConfirmDialogContext,
  type ConfirmOptions,
  type ConfirmTone,
} from './ConfirmDialogContext';
import s from './ConfirmDialogProvider.module.css';

type PendingConfirm = {
  message: string;
  title: string;
  confirmLabel: string;
  cancelLabel: string;
  tone: ConfirmTone;
};

type ConfirmDialogProviderProps = {
  children: ReactNode;
};

/** Mount once near the app root; components ask questions via the useConfirm() hook. */
function ConfirmDialogProvider({ children }: ConfirmDialogProviderProps) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);

  const confirm = useCallback((message: string, options?: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      // A new question supersedes an unanswered one.
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setPending({
        message,
        title: options?.title ?? 'Are you sure?',
        confirmLabel: options?.confirmLabel ?? 'Confirm',
        cancelLabel: options?.cancelLabel ?? 'Cancel',
        tone: options?.tone ?? 'danger',
      });
    });
  }, []);

  const settle = (confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
    setPending(null);
  };

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      <Dialog
        open={Boolean(pending)}
        onClose={() => settle(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '14px',
              width: 420,
              maxWidth: '92vw',
              boxShadow: '0 24px 64px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.08)',
            },
          },
        }}>
        {pending && (
          <div className={s.body}>
            <h3 className={s.title}>{pending.title}</h3>
            <p className={s.message}>{pending.message}</p>
            <div className={s.actions}>
              <button type="button" className={s.cancelBtn} onClick={() => settle(false)}>
                {pending.cancelLabel}
              </button>
              <button
                type="button"
                className={`${s.confirmBtn} ${pending.tone === 'success' ? s.confirmBtnSuccess : ''}`}
                onClick={() => settle(true)}>
                {pending.confirmLabel}
              </button>
            </div>
          </div>
        )}
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
}

export default ConfirmDialogProvider;
